import { Autocomplete, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

import { ButtonAdd } from '../../../components/general/mini_components';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { getServicesInvestigationService } from '../../../services/agenda';
import { addServiceToRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canModifyRequestServices, requestTerminologyKey } from './requestModification';
import { IRequest, IServiceInvestigation, ServiceType } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface AddServiceActionCoreProps extends LocalizeContextProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    requestServiceType?: ServiceType;
    availableServices: IServiceInvestigation[];
    onAdd: (servicesInvestigationId: number[], reason: string) => void | Promise<boolean>;
}

// Presentational core: when the user may modify services, offer an "Add service"
// button that opens the shared reason modal carrying a multi-select picker over
// the investigation's catalogue. Renders nothing when the policy disallows it.
export const AddServiceActionCore: React.FC<AddServiceActionCoreProps> = ({ request, permissions, loading, requestServiceType, availableServices, onAdd, translate }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<IServiceInvestigation[]>([]);

    if (!canModifyRequestServices(request, permissions)) {
        return null;
    }

    const close = () => { setOpen(false); setSelected([]); };

    return (
        <>
            <ReasonModal
                open={open}
                closeModal={close}
                onConfirm={(reason) => onAdd(selected.map((s) => s.id), reason)}
                loading={loading}
                titleId={requestTerminologyKey(requestServiceType, 'add_section')}
                bodyId={requestTerminologyKey(requestServiceType, 'add_modal')}
                reasonLabelId="pages.hospital.services.request.add_service.reason_label"
                reasonTestId="add-reason"
                confirmDisabled={selected.length === 0}
            >
                <Autocomplete
                    multiple
                    options={availableServices}
                    getOptionLabel={(option) => option.description}
                    value={selected}
                    onChange={(_event, newValue) => setSelected(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={translate(requestTerminologyKey(requestServiceType, 'select_label')) as string}
                            inputProps={{ ...params.inputProps, 'data-testid': 'add-service-select' }}
                        />
                    )}
                />
            </ReasonModal>
            <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom>
                <Translate id={requestTerminologyKey(requestServiceType, 'add_section')} />
            </Typography>
            <ButtonAdd data-testid="add-service" disabled={loading} onClick={() => setOpen(true)} />
        </>
    );
};

export const AddServiceActionCoreLocalized = withLocalize(AddServiceActionCore);

interface AddServiceActionProps {
    request: IRequest;
    uuidInvestigation: string;
    requestServiceType: ServiceType;
    permissions: string[];
    onChanged: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: loads the investigation's service catalogue for this request's type
// (via the canonical service helper) and wires the add-service call to the core.
const AddServiceAction: React.FC<AddServiceActionProps> = ({ request, uuidInvestigation, requestServiceType, permissions, onChanged, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onChanged);
    const [availableServices, setAvailableServices] = useState<IServiceInvestigation[]>([]);

    useEffect(() => {
        if (!canModifyRequestServices(request, permissions)) {
            return;
        }
        getServicesInvestigationService(uuidInvestigation, requestServiceType)
            .then((response) => setAvailableServices(response.servicesInvestigation || []))
            .catch(() => setAvailableServices([]));
    }, [uuidInvestigation, requestServiceType]);

    const handleAdd = (servicesInvestigationId: number[], reason: string) =>
        run(() => addServiceToRequestService(uuidInvestigation, request.id, servicesInvestigationId, reason), 'pages.hospital.services.request.add_service.success');

    return (
        <AddServiceActionCoreLocalized
            request={request}
            permissions={permissions}
            loading={loading}
            requestServiceType={requestServiceType}
            availableServices={availableServices}
            onAdd={handleAdd}
        />
    );
};

export default AddServiceAction;
